import { TUpdateAction } from "@fl/shared/src/types/actions";
import { GerenciadorPaginasItens } from "@fl/shared/src/types/menus";
import {
  PaginasMenuAttributes,
  UpdatePaginasMenu,
} from "@fl/shared/src/types/models/suporte_fl/paginas_menus";
import React, { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { BiCollapseVertical, BiExpandVertical } from "react-icons/bi";
import MenuToolManager from "../../../components/menus/manager/index";
import ModalMenu from "../../../components/menus/manager/modal";
import { useMenuContext } from "../../../context/menus";
import { useNotificationContext } from "../../../context/notifications";
import useConfirm from "../../../hooks/useConfirm";
import ApiMenus from "../../../services/suporte/auth/menus";
import { collapseAllMenus, organizeMenus } from "../../../utils/menusFuncs";
import SortFunctions from "../../../utils/sortArrays";
import DefaultBasePage from "../../default/base";

const ConfigMenusPrivatePage: React.FC = () => {
  const { addNotification, setShowNotification } = useNotificationContext();
  const { menus, setMenus } = useMenuContext();

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [menusUpdated, setMenusUpdated] = useState<UpdatePaginasMenu[]>([]);
  const [menu, setMenu] = useState<UpdatePaginasMenu>();
  const [menusDeleted, setMenusDeleted] = useState<UpdatePaginasMenu[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);

  useEffect(() => {
    const _menus: PaginasMenuAttributes[] = menus.sort(
      (a: PaginasMenuAttributes, b: PaginasMenuAttributes) =>
        new SortFunctions(a, b).sortMenus()
    );

    setMenusUpdated(
      _menus.map((item) => {
        return {
          ...item,
          checked: false,
          collapsed: false,
          new: false,
          edited: false,
        };
      })
    );
  }, []);

  useEffect(() => {
    if (menusUpdated.filter((item) => item.edited || item.new).length > 0)
      setHasChanges(true);
  }, [menusUpdated]);

  const onUpdateMenu = (item: UpdatePaginasMenu) => {
    const updated_menus = [...menusUpdated];

    if (item.new) {
      if (item.edited) {
        const index = updated_menus.findIndex(
          (it) => it.id_pagina_menu === item.id_pagina_menu
        );
        updated_menus[index] = { ...item };
      } else {
        updated_menus.push({ ...item });
      }
    } else {
      if (item.edited) {
        const index = updated_menus.findIndex(
          (it) => it.id_pagina_menu === item.id_pagina_menu
        );
        updated_menus[index] = { ...item };
      } else {
        updated_menus.push({ ...item });
      }
    }

    setHasChanges(true);
    setMenusUpdated(updated_menus);
    setShowModal(false);
  };

  const onUpdateItem = (
    item: GerenciadorPaginasItens,
    action: TUpdateAction
  ) => {
    const root = menusUpdated
      .filter((i) => i.id_pagina_menu_pai === item.id_pagina_menu)
      .sort((a, b) => new SortFunctions(a.ordem, b.ordem).sortNumberDesc());
    var ordem = 0;
    if (root && root.length > 0) ordem = root[0].ordem + 1;

    const max_id = menusUpdated.sort((a, b) =>
      new SortFunctions(a.id_pagina_menu, b.id_pagina_menu).sortNumberDesc()
    )[0].id_pagina_menu;

    if (action === "add") {
      setMenu({
        checked: false,
        collapsed: false,
        config: false,
        divisor: false,
        link: "",
        id_pagina_menu_pai: item.id_pagina_menu,
        id_pagina_menu: max_id ? max_id + 1 : undefined,
        new: true,
        edited: false,
        ordem,
        pagina: "",
        createdAt: new Date(),
      });
      setShowModal(true);
    } else if (action === "update") {
      setMenu({
        ...item,
        edited: true,
      });
      setShowModal(true);
    } else if (action === "delete") {
      handleShowPage(
        "Confirmar exclusão de menus?",
        `Tem certeza que deseja excluir o menu "<b>${item.pagina}</b>"?${
          menusUpdated.filter(
            (subItem) => subItem.id_pagina_menu_pai === item.id_pagina_menu
          ).length === 0
            ? ""
            : `Atenção, todos os submenus (<b>${
                menusUpdated.filter(
                  (subItem) =>
                    subItem.id_pagina_menu_pai === item.id_pagina_menu
                ).length
              }</b>) <u>também serão excluídos</u>!`
        }`,
        item
      );
    }
  };

  const submitDeletePage = (item: GerenciadorPaginasItens) => {
    const updatedMenus = menusUpdated.filter(
      (menu) =>
        menu.id_pagina_menu !== item.id_pagina_menu &&
        menu.id_pagina_menu_pai !== item.id_pagina_menu
    );

    setMenusDeleted([...menusDeleted, item]);
    setMenusUpdated(updatedMenus);
    setHasChanges(true);
  };

  const onSaveItems = async () => {
    try {
      if (menusUpdated.length > 0) {
        const initialPages: GerenciadorPaginasItens[] = [];
        const finalPages = [
          ...organizeMenus(
            menusUpdated,
            menusUpdated.filter((item) => item.id_pagina_menu_pai === null),
            initialPages,
            0
          ),
        ].sort((a, b) => new SortFunctions(a.ordem, b.ordem).sortNumberAsc());

        const result = await new ApiMenus({
          updated: finalPages,
          deleted: menusDeleted,
        }).update();

        if (result.message && result.message.length > 0) {
          result.message.forEach((m) => addNotification(m));
          setShowNotification(true);
        }
        if (result.success) {
          const newMenus: UpdatePaginasMenu[] = result.data;
          setMenus(newMenus);
        }
      }
    } finally {
      setHasChanges(false);
      setLoading(false);
    }
  };

  const { handleShow: handleShowPage, ConfirmModalComponent: ConfirmModal } =
    useConfirm<GerenciadorPaginasItens>({
      onConfirm: submitDeletePage,
    });

  return (
    <DefaultBasePage title="Gerenciador de Menus" loading={loading}>
      <div className="text-justify">
        <Container className="d-flex vertical-center my-3">
          <Container>
            <Button
              variant="primary"
              onClick={() => {
                const root = menusUpdated
                  .sort((a, b) => new SortFunctions(a, b).sortMenus())
                  .filter((item) => item.id_pagina_menu_pai === null);

                const max_id = menusUpdated.sort((a, b) =>
                  new SortFunctions(
                    a.id_pagina_menu,
                    b.id_pagina_menu
                  ).sortNumberDesc()
                )[0].id_pagina_menu;

                setMenu({
                  checked: false,
                  collapsed: false,
                  config: false,
                  divisor: false,
                  link: "",
                  id_pagina_menu_pai: null,
                  id_pagina_menu: max_id ? max_id + 1 : undefined,
                  new: true,
                  edited: false,
                  ordem: root[root.length - 1].ordem + 1,
                  pagina: "",
                  createdAt: new Date(),
                });
                setShowModal(true);
              }}
            >
              Adicionar Menu
            </Button>
          </Container>

          <Container className="width-full d-flex d-row justify-content-end">
            {collapseAll ? (
              <div
                className="d-flex vertical-center button strong-hover"
                onClick={() => {
                  const { items, collapse } = collapseAllMenus(
                    menusUpdated,
                    collapseAll
                  );
                  setMenusUpdated(items);
                  setCollapseAll(collapse);
                }}
              >
                <BiExpandVertical size={20} />
                Expandir Todos
              </div>
            ) : (
              <div
                className="d-flex vertical-center button strong-hover"
                onClick={() => {
                  const { items, collapse } = collapseAllMenus(
                    menusUpdated,
                    collapseAll
                  );
                  setMenusUpdated(items);
                  setCollapseAll(collapse);
                }}
              >
                <BiCollapseVertical size={20} />
                Recolher Todos
              </div>
            )}
          </Container>
        </Container>

        <MenuToolManager
          menus={menusUpdated}
          setMenus={setMenusUpdated}
          onUpdate={onUpdateItem}
        />

        {hasChanges && (
          <div className="d-flex justify-content-end">
            <Button
              variant="outline-success"
              className="my-3"
              onClick={onSaveItems}
            >
              Salvar
            </Button>
          </div>
        )}
      </div>

      {ConfirmModal}

      <ModalMenu
        showModal={showModal}
        setShowModal={setShowModal}
        menu={menu}
        setMenu={setMenu}
        onUpdate={onUpdateMenu}
      />
    </DefaultBasePage>
  );
};

export default ConfigMenusPrivatePage;
